import { ChatState } from '@/ai/utils/types';
import { IconType } from '@/types/icon.types';
import { i18n } from '@/common/locale';

// 환영 메시지의 샘플 발화 버튼 색·아이콘 — WELCOME_PROMPT_MESSAGE 발화 개수 이상 유지 필수.
// BotMessage 의 v-for 가 [index] 로 직접 접근하므로, 부족하면 초과분 버튼의 색/아이콘이
// undefined 로 렌더된다 → 4종 팔레트를 GREETING_SLOTS 만큼 cycle 생성.
const GREETING_ICON_CYCLE: IconType[] = [
  'icon-grid2',
  'icon-outlined-line-chart',
  'icon-settings',
  'icon-application-bold',
];
const GREETING_COLOR_CYCLE = [
  'var(--ai-chat-bot-greeting-icon-color-primary)',
  '#F59E0B',
  '#3B82F6',
  '#10B981',
];
const GREETING_HOVER_CYCLE = [
  'var(--ai-chat-bot-greeting-font-color-primary)',
  'var(--ai-chat-bot-greeting-font-color-secondary)',
  'var(--ai-chat-bot-greeting-font-color-tertiary)',
  'var(--ai-chat-bot-greeting-font-color-quaternary)',
];
const GREETING_SLOTS = 12;
const cycle = <T>(arr: T[]): T[] =>
  Array.from({ length: GREETING_SLOTS }, (_, i) => arr[i % arr.length]);

export const ICON_TYPE: IconType[] = cycle(GREETING_ICON_CYCLE);
export const GREETING_COLOR = cycle(GREETING_COLOR_CYCLE);
export const GREETING_HOVER_COLOR = cycle(GREETING_HOVER_CYCLE);

/**
 * 챗봇 창이 비어있을 때 (chatMessages.length === 0) 표시되는 환영 메시지.
 * BotMessage 의 welcomeText 분기가 message + parsedResult 버튼들을 렌더하고,
 * 버튼 클릭 시 handleClick → emit('message-click') → ChatWindow.handleMessageClick →
 * addChatMessage 로 그 발화가 사용자 입력으로 invoke 된다.
 */
export const buildWelcomePromptMessage = () => ({
  messageType: 'welcomeText',
  sender: 'chatbot',
  message: i18n.global.t('WORD.CHATBOT.WELCOME'),
  resultType: 'textButton',
  // 10개 = 7 intent + 신기능 풀커버: 리포트 / 현황 / RCA / 전문가 소환 / 부하 분석 /
  // SQL 분석(PA) / 대시보드 생성 / 대시보드 찾기 / 화면 이동 / 현재 화면 분석.
  // (현황·이동 계열 중복 발화 3건은 2026-06-12 정리 — 같은 도구로 흘러가 답이 겹침)
  result: JSON.stringify([
    { message: i18n.global.t('WORD.CHATBOT.EXAMPLE.YESTERDAY_REPORT') },
    { message: i18n.global.t('WORD.CHATBOT.EXAMPLE.SYSTEM_STATUS') },
    { message: i18n.global.t('WORD.CHATBOT.EXAMPLE.SLOW_INSTANCE_RCA') },
    { message: i18n.global.t('WORD.CHATBOT.EXAMPLE.DB_SPECIALIST') },
    { message: i18n.global.t('WORD.CHATBOT.EXAMPLE.BUSY_HOST') },
    { message: i18n.global.t('WORD.CHATBOT.EXAMPLE.SLOW_SQL') },
    { message: i18n.global.t('WORD.CHATBOT.EXAMPLE.MAKE_DASHBOARD') },
    { message: i18n.global.t('WORD.CHATBOT.EXAMPLE.OPEN_K8S_DASHBOARD') },
    { message: i18n.global.t('WORD.CHATBOT.EXAMPLE.GOTO_WAS_LIST') },
    { message: i18n.global.t('WORD.CHATBOT.EXAMPLE.ANALYZE_SCREEN') },
  ]),
  imageUrl: '',
});

export const DEFAULT_CHAT_STATE: ChatState = {
  button: { top: window.innerHeight - 80, left: window.innerWidth - 80 },
  window: { top: window.innerHeight - 800, left: window.innerWidth - 560 },
  windowScroll: { scrollTop: 0 },
  windowSize: { width: 480, height: 720 },
};

export const RESIZE_DIRECTION = [
  'top',
  'right',
  'bottom',
  'left',
  'top-right',
  'top-left',
  'bottom-right',
  'bottom-left',
];

export const MIN_WIDTH = 480;
export const MIN_HEIGHT = 500;
export const MAX_WIDTH = 740;
export const BUTTON_SIZE = 60;
export const SCREEN_PADDING = 20;
export const WINDOW_SIZE_RATIO = 0.8;

/**
 * 사용자가 응답 생성을 중지했을 때 봇 메시지 끝에 남기는 이력 마커.
 * 백엔드 ChatbotService 가 연결 종료(abort) 턴을 저장할 때 쓰는 문구와 동일해야
 * 스트리밍 화면과 이력 재조회 결과가 어긋나지 않는다 (i18n 미적용 — 저장 본문은
 * 백엔드가 한국어로 고정 생성하므로 locale 별로 갈리면 오히려 불일치).
 */
export const STOPPED_BY_USER_TEXT = '(사용자가 중지함)';
